import React from 'react';
import '@coreui/coreui/dist/css/coreui.min.css';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip
} from 'recharts';
import {
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionBody
} from '@coreui/react';
import {
  AlertTriangle,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Info,
  Terminal,
  Network,
  Code
} from 'lucide-react';
import styled from 'styled-components';

// Styled Components - Exact match with Web component
const Container = styled.div`
  padding: 2rem;
  background: transparent;
  min-height: 100vh;
`;

const SEVERITY_COLORS = {
  Critical: '#3d0808',
  High:     '#d32f2f',
  Medium:   '#FFC300',
  Low:      '#10901f',
  Informational: '#3e99f5',
};

const ChartContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin: 2rem 0;
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 450px;
  &:hover {
    transform: translateY(-4px);
  }
  h5 {
    margin: 0 0 1rem;
    font-size: 1.25rem;
    color: #2d3748;
    font-weight: 600;
    text-align: center;
    width: 100%;
  }
`;

const ChartLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  width: 100%;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: .5rem;
  font-size: .875rem;
  color: #4a5568;
  span.color-box {
    width:12px;
    height:12px;
    border-radius:3px;
  }
`;

const StyledAccordion = styled(CAccordion)`
  margin-top: 2rem;
  background: transparent;
`;

const StyledAccordionItem = styled(CAccordionItem)`
  border: none;
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  background: white;
`;

const StyledAccordionHeader = styled(CAccordionHeader)`
  background: ${({ severity }) => SEVERITY_COLORS[severity]};
  color: white !important;
  border: none;
  padding: 0;
  width:100%;
  &:hover {
    opacity: .95;
  }
  button {
    color: white !important;
    background: transparent!important;
    border:none!important;
    box-shadow:none!important;
    width:100%;
    text-align:left;
    padding:1rem;
    display:flex;
    align-items:center;
    gap:1rem;
    cursor:pointer;
    font-size: 1rem;
    font-weight: 600;
    &::after {
      filter: brightness(0) invert(1);
    }
    &:focus {
      box-shadow:none!important;
    }
  }
`;

const StyledAccordionBody = styled(CAccordionBody)`
  background: white;
  padding: 1.5rem;
`;

const RemediationSection = styled.div`
  background: #f8fafc;
  border-radius:8px;
  padding:1.5rem;
  margin-top:1rem;
  h6 {
    color: #0369a1;
    margin:0 0 1rem;
    font-weight:600;
    font-size: 1.1rem;
    display:flex;
    align-items:center;
    gap:.5rem;
  }
  ul {
    margin:0;
    padding-left:1.5rem;
  }
  li {
    margin-bottom:.5rem;
    color:#334155;
  }
`;

const CodeBlock = styled.pre`
  background:#1a1a1a;
  color:#00ff00;
  padding:1rem;
  border-radius:6px;
  margin:1rem 0;
  font-family:'Courier New', Courier, monospace;
  position:relative;
  width:100%;
  max-width:100%;
  overflow-x:auto;
  white-space:pre-wrap;
  word-wrap:break-word;
  .terminal-header {
    position:absolute;
    top:0;
    left:0;
    right:0;
    background:#333;
    padding:.5rem 1rem;
    display:flex;
    align-items:center;
    gap:.5rem;
    border-top-left-radius:6px;
    border-top-right-radius:6px;
    color:white;
    font-size:.875rem;
  }
  code {
    display:block;
    padding-top:${props => props.hasHeader ? '2.5rem' : '0'};
    max-width:100%;
    overflow-wrap:break-word;
    word-break:break-all;
    font-size:.9rem;
    line-height:1.5;
  }
`;

const DetailsList = styled.dl`
  display:grid;
  grid-template-columns:auto 1fr;
  gap:.5rem 1rem;
  margin:1rem 0;
  background:#f8fafc;
  padding:1rem;
  border-radius:8px;
  dt {
    font-weight:600;
    color:#4a5568;
  }
  dd {
    margin:0;
    color:#2d3748;
  }
`;

const SectionTitle = styled.h3`
  color:#1a202c;
  font-size:1.5rem;
  font-weight:600;
  margin:2rem 0 1rem;
  display:flex;
  align-items:center;
  gap:.75rem;
  svg {
    width:24px;
    height:24px;
  }
`;

const TechnicalDetailsTitle = styled.h6`
  color: #0369a1;
  margin: 1rem 0 0.5rem;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Impact colors for firewall vulnerabilities
const IMPACT_COLORS = {
  'Network Security': '#3e99f5',
  'System Compromise': '#7c3aed',
  'Data Exposure': '#f59e0b'
};

const DynamicFirewallAccordion = ({ data }) => {
  const severityOrder = ['Critical','High','Medium','Low','Informational'];

  const impactMapping = {
    'Open Ports': 'Network Security',
    'ICMP Scan': 'Network Security',
    'Traceroute': 'Network Security',
    'Port Scan': 'Network Security',
    'Service Detection': 'System Compromise',
    'Version Detection': 'Data Exposure',
    'OS Detection': 'System Compromise',
    'Firewall Bypass': 'System Compromise',
    'Network Reconnaissance': 'Data Exposure',
    'ACK Scan': 'Network Security',
    'SYN Scan': 'Network Security',
    'UDP Scan': 'Network Security',
    'FIN Scan': 'Network Security',
    'NULL Scan': 'Network Security',
    'XMAS Scan': 'Network Security'
  };

  // Filter and process vulnerabilities
  const filterValidVulnerabilities = (vulns) =>
    vulns.filter(v => v && typeof v === 'object' && v.isVulnerable);

  const sortBySeverity = (vulns) =>
    vulns.sort((a, b) => severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity));

  const processedData = data.map(item => ({
    ip: item.ip,
    vulnerabilities: sortBySeverity(
      filterValidVulnerabilities(Object.values(item.results || {}))
    )
  }));

  // Build severity counts
  const severityCount = processedData.reduce((acc, item) => {
    item.vulnerabilities.forEach(v => {
      acc[v.severity] = (acc[v.severity] || 0) + 1;
    });
    return acc;
  }, {});

  const chartData = severityOrder
    .map(name => ({ name, value: severityCount[name] || 0 }))
    .filter(d => d.value > 0);

  const processImpactData = () => {
    const impactCount = {};
    processedData.forEach(item => {
      item.vulnerabilities.forEach(v => {
        const impact = impactMapping[v.name] || 'Network Security';
        impactCount[impact] = (impactCount[impact] || 0) + 1;
      });
    });
    return Object.entries(impactCount).map(([name, value]) => ({ name, value }));
  };

  // Custom label for severity chart
  const renderCustomizedLabel = ({ cx, cy, midAngle, outerRadius, index }) => {
    const RAD = Math.PI / 180;
    const r = outerRadius * 1.35;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    const entry = chartData[index];
    return (
      <text x={x} y={y} fill={SEVERITY_COLORS[entry.name]} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="14">
        {`${entry.name}: ${entry.value}`}
      </text>
    );
  };

  // Custom label for impact chart
  const renderImpactLabel = ({ cx, cy, midAngle, outerRadius, index }) => {
    const RAD = Math.PI / 180;
    const r = outerRadius * 1.35;
    const x = cx + r * Math.cos(-midAngle * RAD);
    const y = cy + r * Math.sin(-midAngle * RAD);
    const entry = processImpactData()[index];
    return (
      <text x={x} y={y} fill={IMPACT_COLORS[entry.name]} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="14">
        {`${entry.name}: ${entry.value}`}
      </text>
    );
  };

  const CustomLegend = ({ payload }) => (
    <ChartLegend>
      {payload.map((entry, i) => (
        <LegendItem key={i}>
          <span className="color-box" style={{ backgroundColor: entry.color }} />
          <span>{entry.value} ({entry.payload.value})</span>
        </LegendItem>
      ))}
    </ChartLegend>
  );

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'Critical': return <AlertTriangle color="white" />;
      case 'High':     return <ShieldAlert color="white" />;
      case 'Medium':   return <Shield color="white" />;
      case 'Low':      return <ShieldCheck color="white" />;
      case 'Informational': return <Info color="white" />;
      default:         return <Shield color="white" />;
    }
  };

  const formatPortDetails = (ports) =>
    ports.map(p =>
      `PORT: ${p.Port || 'N/A'}\nSTATE: ${p.State || 'N/A'}\nSERVICE: ${p.Service || 'N/A'}\nVERSION: ${p.Version || 'N/A'}\n───`
    ).join('\n');

  const renderIcmp = (scan) => {
    if (!scan.details || typeof scan.details !== 'string') return null;
    const stats = scan.details.match(/---.*---\n([\s\S]*?rtt min\/avg\/max\/mdev =.*)/);
    if (!stats) return null;
    return (
      <CodeBlock hasHeader>
        <div className="terminal-header">
          <Terminal size={14} />
          <span>ICMP Results</span>
        </div>
        <code>{stats[1]}</code>
      </CodeBlock>
    );
  };

  const renderTranceroute = (scan) => {
    if (!scan.details?.hops) return null;
    const lines = scan.details.hops.map(h => `Hop ${h.hop}: ${h.ip}`).join('\n');
    return (
      <CodeBlock hasHeader>
        <div className="terminal-header">
          <Terminal size={14} />
          <span>Traceroute</span>
        </div>
        <code>{lines}</code>
      </CodeBlock>
    );
  };

  const renderCodeRemediation = (vuln) => {
    if (!vuln.remediation && !vuln.codeRemidiation) return null;
    return (
      <RemediationSection>
        <h6><Code size={18} />Remediation Steps</h6>
        {vuln.remediation && (
          <ul>
            {vuln.remediation.split('. ').map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        )}
        {vuln.codeRemidiation && (
          <>
            <h6><Terminal size={18} />Example Implementation</h6>
            <CodeBlock hasHeader>
              <div className="terminal-header">
                <Terminal size={14} /><span>Terminal</span>
              </div>
              <code>{vuln.codeRemidiation}</code>
            </CodeBlock>
          </>
        )}
      </RemediationSection>
    );
  };

  const renderScanDetails = (scan) => (
    <>
      <DetailsList>
        <dt>Description</dt><dd>{scan.description || 'No description available'}</dd>
        <dt>Scan Type</dt><dd>{scan.name}</dd>
        <dt>Impact</dt><dd>{impactMapping[scan.name] || 'Network Security'}</dd>
      </DetailsList>

      {Array.isArray(scan.details) && scan.details[0]?.Ports?.length > 0 && (
        <CodeBlock hasHeader>
          <div className="terminal-header">
            <Terminal size={14} /><span>Ports</span>
          </div>
          <code>{formatPortDetails(scan.details[0].Ports)}</code>
        </CodeBlock>
      )}

      {scan.name === 'ICMP Scan' && renderIcmp(scan)}
      {scan.name === 'Traceroute' && renderTranceroute(scan)}

      {scan.details && typeof scan.details === 'string' && scan.name !== 'ICMP Scan' && scan.name !== 'Traceroute' && (
        <>
          <TechnicalDetailsTitle>
            <Terminal size={18} />Technical Details
          </TechnicalDetailsTitle>
          <CodeBlock hasHeader>
            <div className="terminal-header">
              <Terminal size={14} /><span>Terminal</span>
            </div>
            <code>{scan.details}</code>
          </CodeBlock>
        </>
      )}

      {renderCodeRemediation(scan)}
    </>
  );

  return (
    <Container>
      <ChartContainer>
        <ChartCard>
          <h5>Firewall Vulnerability Severity Distribution</h5>
          <PieChart width={300} height={300}>
            <Pie
              data={chartData}
              cx="50%" cy="50%"
              innerRadius={60} outerRadius={100}
              dataKey="value"
              labelLine label={renderCustomizedLabel}
            >
              {chartData.map((entry, i) => <Cell key={i} fill={SEVERITY_COLORS[entry.name]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
          <CustomLegend payload={chartData.map(item => ({ value: item.name, color: SEVERITY_COLORS[item.name], payload: item }))} />
        </ChartCard>

        <ChartCard>
          <h5>Network Impact Assessment</h5>
          <PieChart width={300} height={300}>
            <Pie
              data={processImpactData()}
              cx="50%" cy="50%"
              innerRadius={60} outerRadius={100}
              dataKey="value"
              labelLine label={renderImpactLabel}
            >
              {processImpactData().map((entry, i) => <Cell key={i} fill={IMPACT_COLORS[entry.name]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
          <CustomLegend payload={processImpactData().map(item => ({ value: item.name, color: IMPACT_COLORS[item.name], payload: item }))} />
        </ChartCard>
      </ChartContainer>

      {processedData.map((item, idx) => (
        <div key={idx}>
          <SectionTitle>
            <Network />
            Firewall Scan Results for IP: {item.ip || `Target #${idx+1}`}
          </SectionTitle>
          <StyledAccordion alwaysOpen>
            {item.vulnerabilities.map((vuln, i) => (
              <StyledAccordionItem key={`${vuln.name}-${i}`} itemKey={`${idx}-${i}`}>
                <StyledAccordionHeader severity={vuln.severity}>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {getSeverityIcon(vuln.severity)}
                    {vuln.name} – {vuln.severity}
                  </div>
                </StyledAccordionHeader>
                <StyledAccordionBody>
                  {renderScanDetails(vuln)}
                </StyledAccordionBody>
              </StyledAccordionItem>
            ))}
          </StyledAccordion>
        </div>
      ))}
    </Container>
  );
};

export default DynamicFirewallAccordion;
